import { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { DashboardLayout } from '../../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { AlertCircle, CheckCircle2, Database, FolderOpen, Settings, FlaskConical } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function V2Dashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, valRes] = await Promise.all([
          axios.get(`${API_URL}/api/v2/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.post(`${API_URL}/api/v2/settings/validate`, {}, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setStats(statsRes.data);
        setValidation(valRes.data);
      } catch (err) {
        console.error('V2 Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  return (
    <DashboardLayout role="super_admin">
      <div className="space-y-6" data-testid="v2-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Interview Engine V2</h1>
            <p className="text-sm text-muted-foreground mt-1">Independent testing environment — Super Admin only</p>
          </div>
          <Badge variant="outline" className="border-amber-500 text-amber-600 px-3 py-1">
            <FlaskConical className="w-3.5 h-3.5 mr-1.5" />
            Testing Environment
          </Badge>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <Card key={i}><CardContent className="p-6 animate-pulse"><div className="h-16 bg-muted rounded" /></CardContent></Card>)}
          </div>
        ) : stats && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">V2 Questions</p>
                      <p className="text-3xl font-bold mt-1">{stats.active_questions}</p>
                      <p className="text-xs text-muted-foreground mt-1">{stats.total_questions} total</p>
                    </div>
                    <Database className="w-10 h-10 text-blue-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">V2 Categories</p>
                      <p className="text-3xl font-bold mt-1">{stats.active_categories}</p>
                      <p className="text-xs text-muted-foreground mt-1">{stats.total_categories} total</p>
                    </div>
                    <FolderOpen className="w-10 h-10 text-violet-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Configured Questions</p>
                      <p className="text-3xl font-bold mt-1">{stats.configured_question_count}</p>
                      <p className="text-xs text-muted-foreground mt-1">per interview</p>
                    </div>
                    <Settings className="w-10 h-10 text-slate-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Engine Status</p>
                      <p className="text-lg font-bold mt-1 flex items-center gap-2">
                        {stats.question_pool_sufficient ? (
                          <><CheckCircle2 className="w-5 h-5 text-green-500" /> Ready</>
                        ) : (
                          <><AlertCircle className="w-5 h-5 text-amber-500" /> Needs Questions</>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{stats.v2_interviews_total} test interviews</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {validation && (
              <Card>
                <CardHeader><CardTitle className="text-base">Configuration Validation</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Questions</p>
                      <p className="font-semibold">{validation.exact_question_count}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Per Question</p>
                      <p className="font-semibold">{validation.per_question_seconds}s</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Total Time</p>
                      <p className="font-semibold">{validation.total_question_minutes} min</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Max Duration</p>
                      <p className="font-semibold">{validation.max_interview_minutes} min</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Remaining</p>
                      <p className={`font-semibold ${validation.remaining_minutes < 0 ? 'text-red-500' : 'text-green-600'}`}>
                        {validation.remaining_minutes} min
                      </p>
                    </div>
                  </div>
                  {validation.issues?.length > 0 && (
                    <div className="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                      {validation.issues.map((issue, i) => (
                        <p key={i} className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 shrink-0" /> {issue}
                        </p>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
